import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { MAIN_SITE_URL } from "@/utils/constants";
import { generateArticleJsonLd, generateBreadcrumbJsonLd } from "@/utils/seo-schema";

const articlePath = "/artykul/kakobuy-shoes-spreadsheet";
const articleImage = "https://readdy.ai/api/search-image?query=Five%20pairs%20of%20different%20style%20sneakers%20arranged%20in%20a%20row%20on%20warm%20cream%20textured%20surface%2C%20white%20retro%20chunky%20and%20sporty%20shoes%2C%20soft%20natural%20window%20light%2C%20minimalist%20footwear%20collection%20photography%2C%20warm%20beige%20and%20sand%20tones%2C%20sneaker%20catalog%20editorial%2C%20clean%20modern%20aesthetic%2C%20shoe%20display%20concept&width=1200&height=800&seq=kakobuy-article-shoes&orientation=landscape";

const schemas = [
  generateArticleJsonLd({
    headline: "Kakobuy Shoes Spreadsheet — Najlepsze Sneakersy i Obuwie w Jednym Miejscu",
    description: "Kompletny przewodnik po kategorii Shoes w Kakobuy Spreadsheet. Znajdź najlepsze sneakersy, buty sportowe i obuwie codzienne — wszystko z linkami do sprawdzonych ofert.",
    datePublished: "2026-07-01",
    dateModified: "2026-07-21",
    imageUrl: articleImage,
    articlePath,
  }),
  generateBreadcrumbJsonLd([
    { name: "Strona główna", path: "/" },
    { name: "Kakobuy Shoes Spreadsheet", path: articlePath },
  ]),
];

export default function KakobuyShoesSpreadsheetArticle() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-20">
        <article className="w-full px-4 md:px-8 py-10 md:py-16">
          <div className="max-w-3xl mx-auto">
            <nav className="flex items-center gap-2 text-xs text-foreground-600 mb-8 flex-wrap">
              <Link to="/" className="hover:text-foreground-900 transition-colors cursor-pointer">Strona główna</Link>
              <span className="w-3 h-3 flex items-center justify-center"><i className="ri-arrow-right-s-line text-xs"></i></span>
              <span className="text-foreground-900 font-medium">Kakobuy Shoes Spreadsheet</span>
            </nav>

            <header className="mb-10">
              <div className="inline-flex items-center gap-2 bg-accent-100 px-4 py-1.5 rounded-full mb-4">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-footprint-line text-accent-500 text-sm"></i></span>
                <span className="text-xs font-semibold text-accent-700 whitespace-nowrap">Kategoria Shoes</span>
              </div>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 leading-tight mb-4">
                Kakobuy Shoes Spreadsheet — Najlepsze Sneakersy i Obuwie w Jednym Miejscu
              </h1>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                Kompletny przewodnik po kategorii Shoes w Kakobuy Spreadsheet. Znajdź najlepsze sneakersy, buty sportowe i obuwie codzienne — wszystko z linkami do sprawdzonych ofert.
              </p>
              <div className="flex items-center gap-3 text-xs text-foreground-600 flex-wrap">
                <span>Ostatnia aktualizacja: Lipiec 2026</span>
                <span className="text-foreground-400">•</span>
                <span>Czas czytania: 9 minut</span>
              </div>
            </header>

            <div className="w-full h-56 md:h-72 rounded-xl overflow-hidden mb-10">
              <img
                src="https://readdy.ai/api/search-image?query=Five%20pairs%20of%20different%20style%20sneakers%20arranged%20in%20a%20row%20on%20warm%20cream%20textured%20surface%2C%20white%20retro%20chunky%20and%20sporty%20shoes%2C%20soft%20natural%20window%20light%2C%20minimalist%20footwear%20collection%20photography%2C%20warm%20beige%20and%20sand%20tones%2C%20sneaker%20catalog%20editorial%2C%20clean%20modern%20aesthetic%2C%20shoe%20display%20concept&width=1200&height=800&seq=kakobuy-article-shoes&orientation=landscape"
                alt="Kakobuy Shoes Spreadsheet — Sneakersy i obuwie"
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="prose-custom text-sm md:text-base text-foreground-800 leading-relaxed space-y-8">
              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">Dlaczego Shoes to najpopularniejsza kategoria?</h2>
                <p className="mb-3">Kategoria Shoes (buty) to absolutny numer jeden w Kakobuy Spreadsheet — generuje około 35% całego ruchu w arkuszu. Popularność tej kategorii wynika z kilku czynników.</p>
                <p className="mb-3">Po pierwsze, buty na platformach typu Kakobuy są często dostępne w znacznie niższych cenach niż w tradycyjnych sklepach. Po drugie, wybór modeli i stylów jest ogromny — od klasycznych białych sneakersów po limitowane edycje i repliki popularnych modeli.</p>
                <p>Po trzecie, zdjęcia QC (kontroli jakości) są szczególnie przydatne przy zakupie butów — możesz dokładnie obejrzeć szwy, podeszwę i detale przed zatwierdzeniem wysyłki.</p>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">Rodzaje butów dostępne w arkuszu</h2>
                <div className="space-y-3">
                  {[
                    { t: "Classic White Sneakers", d: "Minimalistyczne białe sneakersy — ponadczasowy wybór pasujący do każdej stylizacji. Najlepszy stosunek jakości do ceny.", cena: "100–250 zł" },
                    { t: "Retro & Vintage Runners", d: "Buty w stylu retro inspirowane modelami biegowymi z lat 80. i 90. Charakterystyczny design i wygodna amortyzacja.", cena: "120–280 zł" },
                    { t: "Chunky Dad Shoes", d: "Masywne, odważne sneakersy w stylu chunky — hit streetwearu. Duża podeszwa i wyrazisty design.", cena: "150–350 zł" },
                    { t: "Sport Performance", d: "Buty przeznaczone do biegania i treningów. Technologia amortyzacji i oddychające materiały.", cena: "130–300 zł" },
                    { t: "Casual & Lifestyle", d: "Codzienne buty — od slip-onów po lekkie tenisówki. Wygodne i stylowe na co dzień.", cena: "80–180 zł" },
                  ].map((item) => (
                    <div key={item.t} className="bg-background-100 rounded-lg p-4 border border-background-200/70">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-semibold text-foreground-900">{item.t}</h3>
                        <span className="text-xs text-accent-500 font-medium whitespace-nowrap">{item.cena}</span>
                      </div>
                      <p className="text-xs text-foreground-600">{item.d}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">Na co zwracać uwagę przy zakupie butów?</h2>
                <div className="space-y-2">
                  {[
                    "Rozmiarówka — zawsze sprawdzaj tabelę rozmiarów, chińskie rozmiary różnią się od europejskich",
                    "Zdjęcia QC — poproś o zdjęcia rzeczywistego produktu przed wysyłką, sprawdź szwy i klejenie",
                    "Materiał — upewnij się, że wybrany materiał (skóra naturalna, skóra ekologiczna, siateczka) odpowiada Twoim oczekiwaniom",
                    "Opinie sprzedawcy — sprawdź oceny i komentarze innych kupujących, którzy zamówili ten sam model",
                    "Czas wysyłki — buty to produkty, które czasem wymagają dłuższego czasu przygotowania do wysyłki",
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 bg-accent-100/50 rounded-lg border border-accent-200/60">
                      <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"><i className="ri-check-line text-accent-500 text-sm"></i></span>
                      <span className="text-sm text-foreground-700">{item}</span>
                    </div>
                  ))}
                </div>
              </section>

              <section className="bg-gradient-to-r from-accent-100/60 to-primary-100/60 rounded-2xl p-6 md:p-8 text-center border border-accent-200/40">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-3">Przeglądaj kategorię Shoes już teraz</h2>
                <p className="text-sm text-foreground-700 mb-5 max-w-md mx-auto">Setki modeli sneakersów czeka na Ciebie w arkuszu Kakobuy Spreadsheet. Znajdź swoją idealną parę.</p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <a href={MAIN_SITE_URL} target="_blank" rel="noopener nofollow" className="w-full sm:w-auto bg-accent-500 text-background-50 font-semibold px-8 py-3 rounded-full hover:bg-accent-600 transition-all text-sm whitespace-nowrap cursor-pointer">Otwórz Kakobuy Spreadsheet</a>
                  <Link to="/" className="w-full sm:w-auto bg-background-50 text-foreground-900 font-semibold px-8 py-3 rounded-full border border-background-300/60 hover:bg-background-100 transition-all text-sm whitespace-nowrap cursor-pointer">Powrót na stronę główną</Link>
                </div>
              </section>
            </div>
          </div>
        </article>
      </main>
      <Footer />
      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json" dangerouslySetInnerHTML={{ __html: s }} />
      ))}
    </div>
  );
}